/* eslint-disable no-unused-vars */
// LQC-2254 / LQC-2495 - the POST /Trade/Bond and POST /Trade/Future messages. This is
// the SAME door an OMS comes through: the web app is not building a block, it is
// sending an OMS-shaped trade message and the back end manufactures the block. That is
// why the envelope carries import provenance fields we never populate.
//
// The shape below is the MINIMUM proven to be accepted (confirmed by Mitch in Postman,
// 2026-08-12). Deliberately absent, all confirmed optional:
//   • fileImportId / messageImportFIXId - import provenance, we are neither.
//   • execution { structureID, blockID, counterParties, autoStructure, ... } - an OMS's
//     own identifiers. Omitting it is what keeps the block unassigned, which is the
//     whole point: it lands in the Trade Creation widget for the normal flow.
//   • keyRatios { duration, price, ... } - the sender supplies these; we cannot
//     compute duration client-side.
//   • tradeDetails.openClose / level / transactionNumber. Future has no openClose field
//     at all, so closing a position is out of scope (LQC-2496).
//
// CASING: the proven Postman message used "trade" / "subtype" / "LEI". The names below
// follow the published schema instead ("Trade" / "subType" / "lei"), which ASP.NET binds
// case-insensitively. If the endpoint ever 400s, casing is the first thing to try.

export enum TradeMessageType {
  Trade = "Trade",
}

// One subtype per ticket the front end can write. Others (Swap, Repo) get added here
// as their tickets are built. The API enum carries Description attributes with spaces
// ("Bond Option"), but EnumExtensions binds the member name too, so these match.
export enum TradeMessageSubType {
  Bond = "Bond",
  Future = "Future",
  BondOption = "BondOption",
  Swap = "Swap",
  Swaption = "Swaption",
  RepurchaseAgreement = "RepurchaseAgreement",
}

// "Bloomberg Symbol" carries the space - Mitch's accepted /Trade/Future payload used
// exactly that string, not the "ExchangeSymbol" the ticket description guessed at.
export enum SecurityIdentifierType {
  ISIN = "ISIN",
  BloombergSymbol = "Bloomberg Symbol",
}

// The direction VOCABULARY the endpoints accept, which is NOT the app's display
// vocabulary. Buy / Sell is the whole list: FutureModelService validates through the
// shared ValidateDirection, which accepts only those two, so a future ticket showing
// "Buy Future" still sends "Buy". BuyFuture exists in the API enum and parses, which is
// why sending it returned "Invalid Direction Buy Future" - the message prints the
// enum Description, so it looked like a spacing bug and was not one.
export enum TradeMessageDirection {
  Buy = "Buy",
  Sell = "Sell",
}

// Whether the trade opens or closes a position. Bond and Future have no such field;
// BondOption, Swap and Swaption all require it.
export enum TradeOpenClose {
  Open = "Open",
  Close = "Close",
  Substitute = "Substitute",
}

// Who is sending. originatingEntity + lei come from the SELECTED PORTFOLIO's row
// (customerCode / lei), traderName from the logged-in Auth0 profile.
export interface IClientDetails {
  originatingEntity: string;
  lei: string;
  traderName: string;
}

export interface ISecurityIdentifierDetail {
  identifier: string;
  identifierType: SecurityIdentifierType;
}

// The back end requires tradeDate, direction, nominal and settlementDate (see
// IncomingBondTradeDetailModel). Dates are yyyy-MM-dd. For a future, nominal is the
// CONTRACT COUNT, not a face value.
export interface ITradeDetails {
  tradeDate: string;
  direction: string;
  nominal: number;
  settlementDate: string;
  // The user's External ID (LQC-2941). Omitted when blank.
  transactionNumber?: string;
}

// One allocation per portfolio. v1 sends exactly one, taking the whole nominal.
export interface ITradeAllocation {
  portfolio: string;
  nominal: number;
  // Swap/Swaption Leg 2 nominal. The API stores a missing one as 0 (LQC-2973).
  nominal2?: number;
}

// Bond and Future send the same envelope - only subType, the identifier type and the
// direction vocabulary differ.
export interface ITradeMessage {
  messageType: TradeMessageType;
  subType: TradeMessageSubType;
  clientDetails: IClientDetails;
  security: {
    identifierDetails: ISecurityIdentifierDetail[];
  };
  tradeDetails: ITradeDetails;
  allocations: ITradeAllocation[];
}

export type IBondTradeDetails = ITradeDetails;
export type IBondTradeAllocation = ITradeAllocation;
export type IBondTradeMessage = ITradeMessage;
export type IFutureTradeMessage = ITradeMessage;
